<template>
  <PForm
    nested
    :schema="r$"
    :validate-on="['blur', 'input']"
    class="project-group-model-edit-form flex flex-col gap-y-2"
  >
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <PFormField sr-only label="Projectgroepnaam" name="name">
        <PInput v-model="projectGroup.name" placeholder="Projectgroepnaam" ref="inputName">
          <template #leading>
            <FontAwesomeIcon size="sm" icon="layer-group" title="Projectgroepnaam" />
          </template>
        </PInput>
      </PFormField>
    </div>

    <section class="flex flex-col gap-y-2">
      <div class="flex items-center gap-x-2">
        <PBadge :label="groupProjects.length" size="sm" />
        <span class="text-sm font-medium">Projecten</span>
      </div>
      <div v-if="groupProjects.length" role="list" class="flex flex-col gap-y-2">
        <PCollapsible
          v-for="project in groupProjects"
          :key="project.id"
          role="listitem"
          :model-value="expandedProjectId === project.id"
          :title="project.name"
          :disabled="modelFormStore.isModelOpen(project)"
          @update:model-value="toggleProject(project)"
        >
          <template #header="{ expanded }">
            <div class="flex min-w-0 items-center justify-between gap-x-3">
              <ProjectSummary v-if="!expanded" :project="project" class="min-w-0 break-words" />
              <span v-else class="min-w-0 font-medium break-words">{{ project.name }}</span>
              <span v-if="modelFormStore.isModelOpen(project)" class="shrink-0 text-sm">
                Al geopend
              </span>
            </div>
          </template>
          <ModelFormWrapper
            ref="projectEditors"
            inline
            :model-form-config="{ model: project }"
            @close="onProjectSaved(project)"
          />
        </PCollapsible>
      </div>
      <PAlert color="warning" v-else>
        Er zijn nog geen projecten gekoppeld aan deze projectgroep.
      </PAlert>
    </section>
  </PForm>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import PForm from "@/components/form/PForm.vue"
import { useRegle } from "@regle/core"
import PFormField from "@/components/form/PFormField.vue"
import PInput from "@/components/form/PInput.vue"
import PAlert from "@/components/elements/PAlert.vue"
import PBadge from "@/components/elements/PBadge.vue"
import PCollapsible from "@/components/elements/PCollapsible.vue"
import ProjectSummary from "@/components/misc/ProjectSummary.vue"
import { requiredValidator } from "@/helper/validation/validators"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { ProjectGroup } from "@/models/project-group.model"
import { computed, inject, onMounted, ref, watchEffect } from "vue"
import { ProjectGroupController } from "@/controllers/model-controllers/project-group-controller"
import { upperFirst } from "lodash-es"
import { formFocusHack } from "@/helper/form-helper"
import ModelFormWrapper from "@/components/model_edit_form/ModelEditFormWrapper.vue"
import { useProjectStore } from "@/store/projects.store"
import { useModelFormStore } from "@/store/model-forms.store"
import type { Project } from "@/models/project.model"
import { lazyFieldId } from "@/models/types/lazy-field"
import { storeToRefs } from "pinia"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"

const modelFormStore = useModelFormStore()
const { projects } = storeToRefs(useProjectStore())

const expandedProjectId = ref<string>()
const projectEditors = ref<InstanceType<typeof ModelFormWrapper>[]>([])
const dialogStore = useDialogStore()
const isSwitchingProject = ref(false)

function toggleProject(project: Project) {
  if (
    !project.id ||
    modelFormStore.isModelOpen(project) ||
    isSwitchingProject.value ||
    dialogStore.currentDialog
  )
    return
  const nextProjectId = expandedProjectId.value === project.id ? undefined : project.id
  const editor = projectEditors.value.find((form) => form.modelId === expandedProjectId.value)
  if (editor?.isSaving) return
  if (!editor?.isDirty) {
    expandedProjectId.value = nextProjectId
    return
  }

  dialogStore.currentDialog = {
    type: DialogTypes.CONFIRM_DECLINE,
    title: "Projectwijzigingen opslaan?",
    body: "Wil je de wijzigingen opslaan voordat je dit project inklapt?",
    textConfirm: "Ja, opslaan",
    textDecline: "Nee, wijzigingen verwerpen",
    onConfirm: async () => {
      isSwitchingProject.value = true
      try {
        if (await editor.saveForm()) expandedProjectId.value = nextProjectId
      } catch {
        // The save flow reports the error. Keep the draft open so the user can retry.
      } finally {
        isSwitchingProject.value = false
      }
    },
    onDecline: () => {
      // Unmount the editor to discard its local draft; reopening clones the saved project.
      expandedProjectId.value = nextProjectId
    }
  }
}

function onProjectSaved(project: Project) {
  if (expandedProjectId.value === project.id) expandedProjectId.value = undefined
}

const rules = {
  name: { requiredValidator }
}

const controller: ProjectGroupController = inject("projectGroupController")!

const {
  model: projectGroup,
  initialText,
  isFormInvalid
} = useFormConfigurator<ProjectGroup>({ controller })

const { r$ } = useRegle(projectGroup, rules, { autoDirty: false })
watchEffect(() => {
  isFormInvalid.value = r$.$invalid
})

const groupProjects = computed(() => {
  const groupId = projectGroup.value.id
  if (!groupId) return []
  return projects.value
    .filter((project) => lazyFieldId(project.projectGroup) === groupId)
    .sort((a, b) => a.name.localeCompare(b.name, "nl"))
})

const inputName = ref()
onMounted(() => {
  if (initialText) {
    projectGroup.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, projectGroup, "name")
})
</script>

<style scoped></style>
